import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

import { errorMessageFor } from '../../../features/checkout/utils/checkout-form-helpers';
import {
  NewsletterRateLimitError,
  NewsletterService,
  NewsletterValidationError,
} from '../../services/newsletter.service';
import { NewsletterPopupStateService } from '../../services/newsletter-popup-state.service';
import { ToastService } from '../../services/toast.service';
import { ButtonComponent } from '../button/button.component';
import { InputComponent } from '../input/input.component';
import { NewsletterConsentComponent } from './newsletter-consent.component';
import { NEWSLETTER_COPY } from './newsletter.copy';

export type NewsletterFormVariant = 'footer' | 'hero' | 'sidebar' | 'inline' | 'popup';

/**
 * Formulario de suscripción al newsletter reutilizable. Encapsula el
 * FormGroup, la validación, la llamada al endpoint y el feedback vía
 * toast. Cada host configura layout mediante la prop `variant` y,
 * opcionalmente, su propio heading/description por encima del campo.
 *
 * Contrato B9 (spec docs/specs/newsletter-web-plan.md, W1): cualquier 2xx
 * es éxito y se pinta `SUCCESS`; tras un 429 el botón queda deshabilitado
 * los segundos de `Retry-After`. Lleva el honeypot `website` (W3) y el
 * microcopy de consentimiento bajo el botón (W4), así llegan a todos los hosts.
 */
@Component({
  selector: 'app-newsletter-form',
  imports: [ReactiveFormsModule, InputComponent, ButtonComponent, NewsletterConsentComponent],
  templateUrl: './newsletter-form.component.html',
  styleUrl: './newsletter-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'contents' },
})
export class NewsletterFormComponent {
  variant = input<NewsletterFormVariant>('inline');
  heading = input<string>('');
  description = input<string>('');
  placeholder = input<string>('Tu email');
  ctaLabel = input<string>('Suscribirme');
  inputId = input<string>('newsletter-email');

  subscribed = output<void>();

  private readonly fb = inject(FormBuilder);
  private readonly newsletter = inject(NewsletterService);
  private readonly toast = inject(ToastService);
  private readonly popupState = inject(NewsletterPopupStateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly copy = NEWSLETTER_COPY;
  protected readonly loading = signal(false);
  /** Tras un 429, hasta que pasa `Retry-After`. */
  protected readonly coolingDown = signal(false);
  private cooldownTimer: ReturnType<typeof setTimeout> | null = null;

  protected readonly form = this.fb.nonNullable.group({
    email: this.fb.nonNullable.control('', [
      Validators.required,
      Validators.email,
    ]),
    /** Honeypot (W3): una persona no lo ve ni llega con el tabulador. */
    website: this.fb.nonNullable.control(''),
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.cooldownTimer !== null) clearTimeout(this.cooldownTimer);
    });
  }

  protected get emailControl(): FormControl<string> {
    return this.form.controls.email;
  }

  protected errorFor(): string {
    return errorMessageFor(this.emailControl, 'El email');
  }

  protected submit(): void {
    if (this.loading() || this.coolingDown()) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const email = this.emailControl.value.trim();
    if (!email) return;
    // El honeypot se manda tal cual: el front no decide ni avisa (la API responde 202 igual).
    const website = this.form.controls.website.value;

    this.loading.set(true);
    this.newsletter
      .subscribe(email, website)
      .pipe(
        // Astra p1: si el formulario se destruye con la petición en vuelo (p. ej. el pop-up
        // se cierra), la respuesta no debe crear un toast ni un temporizador huérfanos.
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: () => {
          this.toast.show({ type: 'success', message: NEWSLETTER_COPY.SUCCESS });
          this.form.reset({ email: '', website: '' });
          // W6: el pop-up no sale a quien ya se suscribió, venga de donde venga.
          this.popupState.markSubscribed();
          this.subscribed.emit();
        },
        error: (err: unknown) => {
          if (err instanceof NewsletterRateLimitError) {
            this.startCooldown(err.retryAfterSeconds);
            this.toast.show({ type: 'error', message: NEWSLETTER_COPY.RATE_LIMITED });
            return;
          }
          const message =
            err instanceof NewsletterValidationError
              ? err.message
              : NEWSLETTER_COPY.GENERIC_ERROR;
          this.toast.show({ type: 'error', message });
        },
      });
  }

  private startCooldown(seconds: number): void {
    if (this.cooldownTimer !== null) clearTimeout(this.cooldownTimer);
    this.coolingDown.set(true);
    this.cooldownTimer = setTimeout(() => {
      this.cooldownTimer = null;
      this.coolingDown.set(false);
    }, seconds * 1000);
  }
}
